import React, { useState } from 'react';
import { Button, TextField, Typography, Container, Paper, Box } from '@mui/material';
import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';

// TODO: Move to env variables
const poolData = {
  UserPoolId: "us-east-2_Mbaop1Lpa",
  ClientId: "6fv2mp44hehv8abmop81aotulg",
};

const userPool = new CognitoUserPool(poolData);

interface LoginProps {
  onLogin?: (isAuthenticated: boolean) => void;
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [cognitoUser, setCognitoUser] = useState<CognitoUser | null>(null);
  const [userAttributes, setUserAttributes] = useState<any>(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsLoading(true);
    setError("");

    const user = new CognitoUser({
      Username: email,
      Pool: userPool,
    });

    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    user.authenticateUser(authDetails, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken().getJwtToken();
        localStorage.setItem("id_token", idToken);
        onLogin?.(true);
        setIsLoading(false);
      },
      onFailure: (err) => {
        console.error("Login failed:", err);
        setError(err.message || "An error occurred");
        setIsLoading(false);
      },
      newPasswordRequired: (attributes) => {
        // Remove attributes that Cognito will not allow to be modified
        delete attributes.email;
        delete attributes.email_verified;
        delete attributes.phone_number;
        delete attributes.phone_number_verified;
      
        setCognitoUser(user);
        setUserAttributes(attributes);
        setShowNewPasswordForm(true);
        setIsLoading(false);
      },      
    });
  };

  const handleNewPasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!cognitoUser || !userAttributes) return;

    setIsLoading(true);
    setError("");

    cognitoUser.completeNewPasswordChallenge(newPassword, userAttributes, {
      onSuccess: (session: CognitoUserSession) => {
        const idToken = session.getIdToken().getJwtToken();
        localStorage.setItem("id_token", idToken);
        onLogin?.(true);
        setIsLoading(false);
      },
      onFailure: (err) => {
        console.error("New password challenge failed:", err);
        setError(err.message || "An error occurred during password reset");
        setIsLoading(false);
      },
    });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: '#00539B',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
          }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              color: '#00539B',
              fontWeight: 'bold',
              mb: 3,
            }}
          >
            {showNewPasswordForm ? "Set New Password" : "Welcome Back"}
          </Typography>

          {!showNewPasswordForm ? (
            <form onSubmit={handleLogin} style={{ width: '100%' }}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              <TextField
                label="Password"
                type="password"
                variant="outlined"
                fullWidth
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                disabled={isLoading}
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  height: '48px',
                  background: '#00539B',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                Sign In
              </Button>
            </form>
          ) : (
            <form onSubmit={handleNewPasswordSubmit} style={{ width: '100%' }}>
              <TextField
                label="New Password"
                type="password"
                variant="outlined"
                fullWidth
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                sx={{ mb: 2 }}
              />
              {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                  {error}
                </Typography>
              )}
              <Button
                disabled={isLoading}
                type="submit"
                variant="contained"
                fullWidth
                size="large"
                sx={{
                  height: '48px',
                  background: '#00539B',
                  boxShadow: '0 3px 5px 2px rgba(33, 203, 243, .3)',
                }}
              >
                Set New Password
              </Button>
            </form>
          )}
        </Paper>
      </Container>
    </Box>
  );
};

export default Login;
